import React, { useMemo, useState } from 'react';
import { HBox } from '../layout';
import { Button, ButtonProps } from './Button';

export interface ButtonGroupProps {
  /**
   *  Minimum one button is at least expected.
   */
  buttons: ButtonGroupItem[];
  /**
   * ID of the button that should be set active initially.
   */
  defaultSelected?: string;
}

export interface ButtonGroupItem {
  /**
   * ID should be unique within a button group, is needed to identify each button internally.
   * If not provided the index will be used.
   */
  id?: string;
  /**
   * Arguments inherited from Button.tsx and will be used for the item (button) within the group.
   */
  args: Omit<ButtonProps, 'variant' | 'icon'>;
}

/**
 * Display a group of button where one is always selected.
 *
 * @param buttons minimum one button is at least expected
 * @param defaultSelected will be set by default to first element
 */
export function ButtonGroup({
  buttons,
  defaultSelected = buttons[0].id ?? '0'
}: ButtonGroupProps): JSX.Element {
  const [activeButton, setActiveButton] = useState(defaultSelected);
  const memoizedButtons = useMemo(
    () =>
      buttons.map((entry, index) => {
        const id = entry.id ?? index.toString();
        return (
          <Button
            {...entry.args}
            key={id}
            variant="gray"
            onClick={() => {
              if (id != activeButton) {
                setActiveButton(id);
                entry.args.onClick?.();
              }
            }}
            className={activeButton === id ? 'selected' : ''}
          />
        );
      }),
    [activeButton, buttons]
  );
  return (
    <HBox className="ehs-grouped-button" role="group">
      {memoizedButtons}
    </HBox>
  );
}
